import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useReverIntegrationQuery } from "modules/settings/hooks/rever/useReverIntegrationQuery"
import { useUpsertReverIntegrationMutation } from "modules/settings/hooks/rever/useUpsertReverIntegrationMutation"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertReverIntegrationSchema } from "schema"
import { z } from "zod"

export const ReverAuthModal = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpsertReverIntegrationSchema>>({
    resolver: zodResolver(UpsertReverIntegrationSchema),
  })
  const navigate = useNavigate()
  const { mutateAsync } = useUpsertReverIntegrationMutation()
  const { data } = useReverIntegrationQuery()

  const onSubmit = async (data: z.infer<typeof UpsertReverIntegrationSchema>) => {
    await mutateAsync(data)
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/rever" })
    }
  }

  useEffect(() => {
    if (data) {
      form.reset({
        apiKey: data.apiKey ?? "",
      })
    }
  }, [data, form])

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.rever.authModal.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.rever.authModal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <div className="flex flex-col w-full gap-4">
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("settings.integrations.rever.authModal.form.apiKey")}</FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button disabled={form.formState.isLoading || !form.formState.isValid}>
                {t("settings.integrations.rever.authModal.form.submit")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
